import { SentimentVeryDissatisfied } from '@mui/icons-material';
import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem } from '@mui/lab';
import { Card, CardContent, CardHeader, Divider, Typography } from '@mui/material';
import { format } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Check as CheckIcon } from '../../../../icons/check';
import { VisitState } from '../../api/types/visits';
import { useVisitLogs } from '../../hooks/query/use-visit-logs';

import { DataStatusIndicator } from '@/components/molecules/data-status-indicator/data-status-indicator';

interface Props {
  visitState: VisitState;
  visitorId: number;
}

export const VisitTimeline = ({ visitorId, visitState }: Props) => {
  const { t } = useTranslation(['visit']);

  const isFinalStatus = () => {
    if (visitState === VisitState.FINISHED) {
      return true;
    } else return false;
  };

  const { data, isLoading, error } = useVisitLogs(visitorId);

  return (
    <Card variant="outlined">
      <CardHeader title={t('visit:timeline.cardTitle')} />
      <Divider />
      <CardContent>
        {data?.length ? (
          <Timeline
            sx={{
              my: 0,
              p: 0,
            }}>
            {data.map((item, index) => (
              <React.Fragment key={index}>
                <TimelineItem
                  sx={{
                    alignItems: 'center',
                    minHeight: 'auto',
                    '&::before': {
                      display: 'none',
                    },
                  }}>
                  <TimelineDot
                    sx={{
                      backgroundColor: 'success.main',
                      borderColor: 'success.main',
                      color: 'success.contrastText',
                      alignSelf: 'center',
                      boxShadow: 'none',
                      flexShrink: 0,
                      height: 36,
                      width: 36,
                      m: 0,
                    }}
                    variant={'filled'}>
                    <CheckIcon />
                  </TimelineDot>
                  <TimelineContent>
                    <Typography color={'textPrimary'} variant="overline">
                      {t(`visit:timeline.${item.direction}`)} - {item.accessControlId}{' '}
                      {format(new Date(item.occurredAt), 'dd-MM HH:mm')}
                    </Typography>

                    <Typography sx={{ color: 'text.secondary' }} fontSize={12}>
                      {t('visit:timeline.device')} : {item.deviceId} <br />{' '}
                      {t('visit:timeline.entrance')} : {item.entranceId}
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
                {index + 1 < data?.length && (
                  <TimelineConnector
                    sx={{
                      backgroundColor: 'neutral.200',
                      height: 22,
                      ml: 2.25,
                      my: 1,
                    }}
                  />
                )}
              </React.Fragment>
            ))}
            <TimelineConnector
              sx={{
                backgroundColor: 'neutral.200',
                height: 22,
                ml: 2.25,
                my: 1,
              }}
            />
            <TimelineItem
              sx={{
                alignItems: 'center',
                minHeight: 'auto',
                '&::before': {
                  display: 'none',
                },
              }}>
              {isFinalStatus() ? (
                <TimelineDot
                  sx={{
                    backgroundColor: 'success.main',
                    borderColor: 'success.main',
                    color: 'success.contrastText',
                    alignSelf: 'center',
                    boxShadow: 'none',
                    flexShrink: 0,
                    height: 36,
                    width: 36,
                    m: 0,
                  }}
                  variant={'filled'}>
                  <CheckIcon />
                </TimelineDot>
              ) : (
                <TimelineDot
                  sx={{
                    alignSelf: 'center',
                    boxShadow: 'none',
                    flexShrink: 0,
                    height: 36,
                    width: 36,
                    m: 0,
                  }}
                  variant={'filled'}>
                  <CheckIcon />
                </TimelineDot>
              )}
              <TimelineContent>
                <Typography color={'textPrimary'} variant="overline">
                  {t('visit:timeline.finished')}
                </Typography>
              </TimelineContent>
            </TimelineItem>
          </Timeline>
        ) : (
          <Typography
            sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}
            variant="overline">
            <SentimentVeryDissatisfied sx={{ mr: 1 }} />
            {t('visit:timeline.noLogs')}
          </Typography>
        )}
      </CardContent>
      <DataStatusIndicator
        dataLength={data?.length || 0}
        error={error?.response?.data?.message}
        isFetching={isLoading}
      />
    </Card>
  );
};
